import { ehQuantidadeZero, hasValue, isNo } from "./data";
import { F } from "./gerencialFields";
import { toDate } from "./status";
import { TERCEIROS_ITEMS, descricaoItemPendente } from "./terceiros";
import { ACABAMENTO_ITEMS } from "./acabamento";

/**
 * Separa o nome do marceneiro do seu regime, quando a planilha grava o
 * regime como sufixo entre parênteses. Ex.: "NALDO (EMPREITA)" →
 * { nome: "Naldo", tipo: "Empreita" }. Sem sufixo, `tipo` é null.
 *
 * Normaliza a caixa (Title Case) para exibição consistente com o mockup.
 */
function tituloCase(texto) {
  return String(texto || "")
    .toLowerCase()
    .replace(/\b\p{L}/gu, (c) => c.toUpperCase());
}

export function parseMarceneiro(bruto) {
  const raw = String(bruto ?? "").trim();
  if (!raw || raw.toLowerCase() === "sem marceneiro") {
    return { nome: "Sem marceneiro", tipo: null, semMarceneiro: true };
  }
  const match = raw.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (match) {
    return {
      nome: tituloCase(match[1].trim()) || raw,
      tipo: tituloCase(match[2].trim()),
      semMarceneiro: false,
    };
  }
  return { nome: tituloCase(raw), tipo: null, semMarceneiro: false };
}

function hasDateValue(value) {
  return Boolean(toDate(value));
}

export function isEntregaCandidate(row) {
  if (!isNo(row?.[F.OBRA_FUTURA])) return false;
  if (hasValue(row?.[F.CARREGAMENTO])) return false;
  return true;
}

export function getEntregaViewId(row) {
  if (hasDateValue(row?.[F.MARCENARIA_TERMINO])) return "pode_entregar";
  if (hasDateValue(row?.[F.DATA_DE_USINAGEM])) return "em_marcenaria";
  if (hasDateValue(row?.[F.OTIMIZACAO_DO_LOTE])) return "em_maquinas";
  return null;
}

/**
 * Ambiente que ainda nao entrou na producao: candidato a entrega (obra
 * liberada, sem carregamento), fora de qualquer visao da fila
 * (getEntregaViewId === null) e sem data de otimizacao do lote (col. S)
 * nem numero de lote (col. Y). A checagem da fila garante que o mesmo
 * ambiente nunca entra duas vezes no calendario — uma linha com usinagem
 * ou termino de marcenaria lancados sem lote e erro de digitacao, e a
 * fila ja a mostra. O calendario so mostra o ambiente sem lote sob
 * demanda, na data de entrega prevista em contrato (col. N).
 */
export function isEntregaSemLote(row) {
  if (!isEntregaCandidate(row)) return false;
  if (getEntregaViewId(row) !== null) return false;
  if (hasValue(row?.[F.NUMERO_DO_LOTE])) return false;
  return true;
}

/**
 * Verdadeiro quando o valor da celula representa um item esperado naquela
 * etapa (numero inteiro positivo, texto descritivo ou data — qualquer coisa
 * exceto vazio ou marcador "X").
 */
function ehItemEsperado(value) {
  if (!hasValue(value)) return false;
  const s = String(value).trim();
  if (!s) return false;
  if (s.toUpperCase() === "X") return false;
  // Zero da fórmula espelho = nenhum item desse tipo (ver ehQuantidadeZero).
  if (ehQuantidadeZero(value)) return false;
  return true;
}

/**
 * Decide se um ambiente tem itens de Terceiros pendentes/recebidos.
 *
 * Regra (definida pelo usuario):
 *  - Se alguma das colunas BI-BN (TERCEIROS_ITEMS[i].field) tem numero/data/texto
 *    (nao X), o ambiente tem itens de terceiros esperados.
 *  - Se BO (RECEBIMENTO_TERCEIROS) tem data → todos recebidos (verde).
 *  - Se tem itens mas BO vazio → pendente (vazio).
 *  - Se nenhum item → nao aplicavel (oculto).
 *
 * Retorna { aplicavel, completo, itens: [{ label, descricao }] }
 */
export function statusTerceirosAmbiente(amb) {
  if (!amb) return { aplicavel: false };
  const itensEsperados = TERCEIROS_ITEMS.filter((item) =>
    ehItemEsperado(amb[item.field])
  );
  if (itensEsperados.length === 0) return { aplicavel: false };
  const recebimento = toDate(amb[F.RECEBIMENTO_TERCEIROS]);
  return {
    aplicavel: true,
    completo: Boolean(recebimento),
    itens: itensEsperados.map((item) => ({
      label: item.label,
      descricao: descricaoItemPendente(amb, item),
    })),
  };
}

/**
 * Mesma logica para itens de Acabamento.
 *
 *  - Se alguma das colunas de PINTURA/VERNIZ/PINTURA_PERFIL/PINTURA_SERRALHERIA
 *    tem numero/data/texto (nao X), o ambiente tem itens de acabamento esperados.
 *  - Se CE (ACABAMENTO_REALIZADO) tem data → todos finalizados (verde).
 *  - Se tem itens mas CE vazio → pendente (vazio).
 *  - Se nenhum item → nao aplicavel (oculto).
 */
export function statusAcabamentoAmbiente(amb) {
  if (!amb) return { aplicavel: false };
  const itensEsperados = ACABAMENTO_ITEMS.filter((item) =>
    ehItemEsperado(amb[item.field])
  );
  if (itensEsperados.length === 0) return { aplicavel: false };
  const realizado = toDate(amb[F.ACABAMENTO_REALIZADO]);
  return {
    aplicavel: true,
    completo: Boolean(realizado),
    itens: itensEsperados.map((item) => {
      const value = amb[item.field];
      let descricao = "";
      if (hasValue(value)) {
        const s = String(value).trim();
        if (s.toUpperCase() !== "X") descricao = s;
      }
      return { label: item.label, descricao };
    }),
  };
}
